// =============================================================================
// client/src/components/flux/BodyEditor.jsx | v1.0 | S90 | P12A item 7 (C1c/C2)
// =============================================================================
// The campaign body editor. Replaces the raw HTML textarea.
//
// 🔴 THE ACCEPTANCE BAR IS JAKE'S, IN HIS WORDS: "we sure as shit weren't
// writing in HTML." Football staff must not be expected to author markup, and
// the thing that ships to a coach must still be deterministic HTML the
// send-time merge pipeline can splice. Those two pull against each other and
// the schema is what reconciles them: the allowed formatting surface is
// DECLARED in editor-config.js, so what an operator can produce and what the
// engine can consume are the same set by construction rather than by care.
//
// ⚠ WHAT THIS FILE DELIBERATELY DOES NOT DO:
//  · No tracking or machine-only markup is reachable here. The open pixel, the
//    click wrapper, the unsubscribe footer and the intentional 1200px spacer
//    are appended at SEND time by flux-engine.js · appendUnsubscribeLink, to a
//    COPY. They are not body content and must never become editable — that is
//    the authenticity law, and stripSendTimeArtifacts() on paste is what keeps
//    it true against the one path that could break it.
//  · No image, table, heading or raw-HTML node. A recruiting email is
//    paragraphs, emphasis and links. Every node absent from the schema is a
//    node a coach can never receive by accident.
// =============================================================================

import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { CAMPAIGN_BODY_EXTENSIONS, stripSendTimeArtifacts } from '../../lib/flux/editor-config.js';

// The toolbar is short on purpose. Every control here is a node or mark the
// schema already allows; adding a button without adding the mark would render a
// control that silently does nothing, which is worse than no control.
function ToolbarButton({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}   // keep the selection; the click must not blur first
      onClick={onClick}
      className={`text-xs px-2 py-1 rounded border disabled:opacity-40 ${
        active ? 'bg-hunter text-white border-hunter' : 'border-gray-300 text-hunter'
      }`}
    >
      {children}
    </button>
  );
}

const BodyEditor = forwardRef(function BodyEditor({ value, onChange, disabled }, ref) {
  const editor = useEditor({
    extensions: CAMPAIGN_BODY_EXTENSIONS,
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'prose-sm max-w-none min-h-[16rem] px-3 py-2 focus:outline-none',
      },
      // 🔴 PASTE SANITIZATION HAPPENS HERE, BEFORE THE SCHEMA SEES IT.
      // The schema alone already drops most of what a paste carries — it has no
      // div, img or table node, so the 1200px spacer and the open pixel die on
      // ingest without anything being written to stop them.
      //
      // It does NOT drop the unsubscribe anchor, because <a> IS in the schema.
      // Measured, not assumed: round-tripping appendUnsubscribeLink's own output
      // keeps `flux/unsubscribe` and loses `1200px`. So an operator pasting a
      // delivered email back in to reuse it — the most natural thing in the
      // world — would carry the footer link into the body, and the next send
      // would append a SECOND one. A coach receiving two opt-out links is a
      // compliance defect, and this is the line that prevents it.
      transformPastedHTML: (html) => stripSendTimeArtifacts(html),
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Load a body that changed underneath us — the edit-mode fetch resolving, or
  // AI variants being applied. Guarded on inequality so we do not stomp the
  // caret on every keystroke: onUpdate pushes up, this pushes down, and without
  // the guard they fight.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  // ── C2: CURSOR-POSITION INSERTION ─────────────────────────────────────────
  // The textarea path spliced at selectionStart/selectionEnd. That behaviour is
  // carried across rather than regressed to append-at-end: insertContentAt with
  // the live selection is the ProseMirror equivalent, and `from`/`to` covers a
  // RANGE so inserting over a selection replaces it, exactly as the textarea did.
  useImperativeHandle(ref, () => ({
    insertToken(token) {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      editor.chain().focus().insertContentAt({ from, to }, token).run();
    },
  }), [editor]);

  if (!editor) return null;

  return (
    <div className={`mt-1 border rounded ${disabled ? 'bg-gray-50 border-gray-200' : 'border-gray-300'}`}>
      <div className="flex gap-1 flex-wrap border-b border-gray-200 px-2 py-1">
        <ToolbarButton title="Bold" disabled={disabled} active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></ToolbarButton>
        <ToolbarButton title="Italic" disabled={disabled} active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarButton>
        <ToolbarButton title="Bulleted list" disabled={disabled} active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</ToolbarButton>
        <ToolbarButton
          title="Link the selected words"
          disabled={disabled}
          active={editor.isActive('link')}
          onClick={() => {
            if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
            // eslint-disable-next-line no-alert
            const url = window.prompt('Link address (https://…)');
            if (!url) return;
            // Same rule the link slots enforce: http(s) only, and a bad address
            // is refused rather than written into something a coach clicks.
            let parsed;
            try { parsed = new URL(url.trim()); } catch { return; }
            if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
            editor.chain().focus().setLink({ href: parsed.href }).run();
          }}
        >Link</ToolbarButton>
        <span className="flex-1" />
        <ToolbarButton title="Undo" disabled={disabled || !editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}>↶</ToolbarButton>
        <ToolbarButton title="Redo" disabled={disabled || !editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}>↷</ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default BodyEditor;
